import { useId, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label, RequiredMark } from '@/components/ui/label';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import {
  ConnectionSelect,
  CreateConnectionSheet,
} from '@/features/connections';
import { useWorkflows } from '@/features/workflows';
import type { TriggerProfileView } from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';
import { DraftNote } from '@/shared/components/DraftNote';
import {
  useDraftAutosave,
  useRestoredDraft,
} from '@/shared/hooks/useFormDraft';
import { useTriggerDraftStore } from '../drafts/triggerDraftStore';

import { isTriggerError } from '../errors';
import {
  ANY_STEP,
  JiraTriggerConfigSchema,
  ScheduledTriggerConfigSchema,
  TRIGGER_PROFILE_FORM_DEFAULT_VALUES,
  WorkspaceTaskChangeConfigSchema,
  fromTriggerActionWire,
  jiraTriggerProfileRawFieldsSchema,
  scheduleHasCronExpressionWhenRequired,
  scheduleHasPositiveIntervalWhenRequired,
  scheduleHasTimezoneWhenRequired,
  scheduledTriggerProfileRawFieldsSchema,
  triggerActionFormSchema,
  withDefaultedMode,
} from '../types';
import type {
  JiraTriggerProfileFormValues,
  ScheduledTriggerProfileFormValues,
  ScheduledTriggerProfileType,
  TriggerActionKind,
  TriggerProfileFormValues,
  WatchedEvent,
  WorkspaceTaskChangeMode,
} from '../types';
import { ScheduleFields } from './ScheduleFields';
import { TargetWorkflowSelect } from './TargetWorkflowSelect';
import { TriggerActionFields } from './TriggerActionFields';
import { WorkflowSelect } from './WorkflowSelect';
import { WorkflowStepSelect } from './WorkflowStepSelect';

const WATCHED_EVENT_CHOICES: { value: WatchedEvent; label: string }[] = [
  { value: 'opened', label: 'Opened' },
  { value: 'comment', label: 'Comments' },
  { value: 'review', label: 'Reviews' },
  { value: 'commit', label: 'Commits' },
  { value: 'status', label: 'Status changes' },
  { value: 'merged', label: 'Merged' },
  { value: 'pipeline_failed', label: 'Pipeline failed' },
  { value: 'conflict', label: 'Merge request in conflict' },
];

type Driver = 'workspace_task_change' | ScheduledTriggerProfileType | 'jira';

const DRIVER_CHOICES: { value: Driver; label: string; consequence: string }[] =
  [
    {
      value: 'workspace_task_change',
      label: 'Task moved in this workflow',
      consequence:
        'Fires manually whenever a task crosses a chosen step transition.',
    },
    {
      value: 'github',
      label: 'GitHub',
      consequence: 'Watches repositories on a GitHub connection on a schedule.',
    },
    {
      value: 'gitlab',
      label: 'GitLab',
      consequence: 'Watches repositories on a GitLab connection on a schedule.',
    },
    {
      value: 'jira',
      label: 'Jira',
      consequence: 'Watches a Jira project/issue filter on a schedule.',
    },
  ];

/**
 * Create a Trigger Profile, or edit an existing one's non-`type` details
 * (spec 074, FR-001/FR-002) — the driver-specific config UI reopened,
 * pre-populated, rather than a generic form (mirrors `ConnectionFormSheet`'s
 * own create/edit split).
 *
 * `type` is fixed in edit mode (FR-003 — immutable after creation): the
 * driver chooser renders as read-only text, exactly like `ConnectionFormSheet`
 * renders a fixed provider in edit mode. `enabled` is not part of this form
 * at all in either mode — creation defaults it server-side and editing it is
 * `useSetTriggerProfileEnabled`'s own separate mutation (contracts/
 * trigger-profile-lifecycle-api.md).
 */
export type TriggerProfileFormMode =
  { kind: 'create' } | { kind: 'edit'; profile: TriggerProfileView };

/**
 * The sheet's own three-way form state — one `driver` discriminant plus a
 * `config` sub-object whose shape depends on it. Combines the two *sibling*
 * domain shapes (`TriggerProfileFormValues`, `ScheduledTriggerProfileFormValues`;
 * `types.ts`'s package-level note) for exactly the UI mechanics one sheet
 * with one provider chooser needs — deliberately local to this component,
 * not exported from `types.ts`: the domain shapes stay clean two-shape
 * siblings, and only this sheet needs a third, UI-only wrapper to switch
 * between them.
 *
 * `config` is nested (rather than `workflowId`/`connectionId`/etc. sitting
 * at the top level next to `driver`) for the same reason the
 * old connection form nested its own provider-specific fields under `config`: a dotted RHF path
 * into a field that exists in only one union branch (`register('workflowId')`
 * when the active branch is `github`) does not type-check against a
 * discriminated union — RHF's `FieldPath` generation requires the path be
 * valid across the whole type. Nesting means `config` itself is a single
 * common path (`setValue('config', ...)`), and its branch-specific contents
 * are read through a plain structural narrow (`'workflowId' in config`),
 * exactly like `ConnectionFormSheet` already does for its own `config`.
 */
export type SheetFormValues =
  | {
      driver: 'workspace_task_change';
      name: string;
      description: string | null;
      enabled: boolean;
      action: TriggerProfileFormValues['action'];
      config: Pick<
        TriggerProfileFormValues,
        | 'mode'
        | 'workflowId'
        | 'fromStepId'
        | 'toStepId'
        | 'scheduleType'
        | 'intervalSeconds'
        | 'cronExpression'
        | 'timezone'
      >;
    }
  | {
      driver: ScheduledTriggerProfileType;
      name: string;
      description: string | null;
      enabled: boolean;
      action: TriggerProfileFormValues['action'];
      config: Omit<
        ScheduledTriggerProfileFormValues,
        'name' | 'description' | 'enabled' | 'type' | 'action'
      >;
    }
  | {
      driver: 'jira';
      name: string;
      description: string | null;
      enabled: boolean;
      action: TriggerProfileFormValues['action'];
      config: Omit<
        JiraTriggerProfileFormValues,
        'name' | 'description' | 'enabled' | 'type' | 'action'
      >;
    };

/**
 * `workspace_task_change`'s `config` sub-object (Spec 082): a
 * `z.discriminatedUnion('mode', ...)` mirroring `triggerProfileFormSchema`'s
 * own two branches, picked/reassembled here rather than reusing
 * `triggerProfileFormSchema` directly — this sheet's `config` sub-object
 * excludes `name`/`description`/`enabled`/`action` (those sit at the
 * sheet's top level, shared across every driver; see `SheetFormValues`'s
 * own doc comment on why `config` is nested).
 *
 * The event branch also refuses `fromStepId`/`toStepId` both being
 * `ANY_STEP` (spec 074, US5, FR-019) — mirrors the backend's own
 * `EventModeConfig` model-validator message verbatim, so the inline,
 * save-blocking refusal reads identically whether it fires here (before the
 * request is even sent) or as a server error after a request that somehow
 * bypassed this client-side check. Schedule mode has no from/to-step fields
 * at all, so the refinement only applies to the event branch.
 */
const workspaceTaskChangeConfigSchema = z.discriminatedUnion('mode', [
  z
    .object({
      mode: z.literal('event'),
      workflowId: z.string().min(1, 'Choose a workflow.'),
      fromStepId: z.string().min(1, 'Choose a from-step.'),
      toStepId: z.string().min(1, 'Choose a to-step.'),
      scheduleType: z.enum(['interval', 'cron']),
      intervalSeconds: z.number().nullable(),
      cronExpression: z.string().nullable(),
      timezone: z.string().nullable(),
    })
    .refine(
      (values) =>
        !(values.fromStepId === ANY_STEP && values.toStepId === ANY_STEP),
      {
        message:
          "from_step_id and to_step_id cannot both be 'any_step' — at least one side must name a concrete step.",
        path: ['toStepId'],
      },
    ),
  z
    .object({
      mode: z.literal('schedule'),
      workflowId: z.string(),
      fromStepId: z.string(),
      toStepId: z.string(),
      scheduleType: z.enum(['interval', 'cron']),
      intervalSeconds: z.number().nullable(),
      cronExpression: z.string().nullable(),
      timezone: z.string().nullable(),
    })
    .superRefine((values, ctx) => {
      if (!scheduleHasPositiveIntervalWhenRequired(values)) {
        ctx.addIssue({
          code: 'custom',
          message: 'A positive interval is required.',
          path: ['intervalSeconds'],
        });
      }
      if (!scheduleHasCronExpressionWhenRequired(values)) {
        ctx.addIssue({
          code: 'custom',
          message: 'A cron expression is required.',
          path: ['cronExpression'],
        });
      }
      if (!scheduleHasTimezoneWhenRequired(values)) {
        ctx.addIssue({
          code: 'custom',
          message: 'A timezone is required.',
          path: ['timezone'],
        });
      }
    }),
]);

const scheduledConfigSchema = scheduledTriggerProfileRawFieldsSchema
  .pick({
    connectionId: true,
    repositories: true,
    watch: true,
    scheduleType: true,
    intervalSeconds: true,
    cronExpression: true,
    timezone: true,
    targetWorkflowId: true,
  })
  .refine(scheduleHasPositiveIntervalWhenRequired, {
    message: 'A positive interval is required.',
    path: ['intervalSeconds'],
  })
  .refine(scheduleHasCronExpressionWhenRequired, {
    message: 'A cron expression is required.',
    path: ['cronExpression'],
  })
  .refine(scheduleHasTimezoneWhenRequired, {
    message: 'A timezone is required.',
    path: ['timezone'],
  });

const jiraConfigSchema = jiraTriggerProfileRawFieldsSchema
  .pick({
    connectionId: true,
    project: true,
    issueType: true,
    status: true,
    labels: true,
    scheduleType: true,
    intervalSeconds: true,
    cronExpression: true,
    timezone: true,
    targetWorkflowId: true,
  })
  .refine(scheduleHasPositiveIntervalWhenRequired, {
    message: 'A positive interval is required.',
    path: ['intervalSeconds'],
  })
  .refine(scheduleHasCronExpressionWhenRequired, {
    message: 'A cron expression is required.',
    path: ['cronExpression'],
  })
  .refine(scheduleHasTimezoneWhenRequired, {
    message: 'A timezone is required.',
    path: ['timezone'],
  });

const sheetFormSchema = z.discriminatedUnion('driver', [
  z.object({
    driver: z.literal('workspace_task_change'),
    name: z.string().trim().min(1, 'Name is required.'),
    description: z.string().nullable(),
    enabled: z.boolean(),
    action: triggerActionFormSchema,
    config: workspaceTaskChangeConfigSchema,
  }),
  z.object({
    driver: z.literal('github'),
    name: z.string().trim().min(1, 'Name is required.'),
    description: z.string().nullable(),
    enabled: z.boolean(),
    action: triggerActionFormSchema,
    config: scheduledConfigSchema,
  }),
  z.object({
    driver: z.literal('gitlab'),
    name: z.string().trim().min(1, 'Name is required.'),
    description: z.string().nullable(),
    enabled: z.boolean(),
    action: triggerActionFormSchema,
    config: scheduledConfigSchema,
  }),
  z.object({
    driver: z.literal('jira'),
    name: z.string().trim().min(1, 'Name is required.'),
    description: z.string().nullable(),
    enabled: z.boolean(),
    action: triggerActionFormSchema,
    config: jiraConfigSchema,
  }),
]) satisfies z.ZodType<SheetFormValues>;

function workspaceTaskChangeDefaults(
  name: string,
  description: string | null,
  enabled: boolean,
): SheetFormValues {
  return {
    driver: 'workspace_task_change',
    name,
    description,
    enabled,
    action: TRIGGER_PROFILE_FORM_DEFAULT_VALUES.action,
    config: {
      mode: 'event',
      workflowId: '',
      fromStepId: '',
      toStepId: '',
      scheduleType: 'interval',
      intervalSeconds: null,
      cronExpression: null,
      timezone: null,
    },
  };
}

function scheduledDefaults(
  driver: ScheduledTriggerProfileType,
  name: string,
  description: string | null,
  enabled: boolean,
): SheetFormValues {
  return {
    driver,
    name,
    description,
    enabled,
    action: TRIGGER_PROFILE_FORM_DEFAULT_VALUES.action,
    config: {
      connectionId: '',
      repositories: [],
      watch: [],
      scheduleType: 'interval',
      intervalSeconds: null,
      cronExpression: null,
      timezone: null,
      targetWorkflowId: '',
    },
  };
}

function jiraDefaults(
  name: string,
  description: string | null,
  enabled: boolean,
): SheetFormValues {
  return {
    driver: 'jira',
    name,
    description,
    enabled,
    action: TRIGGER_PROFILE_FORM_DEFAULT_VALUES.action,
    config: {
      connectionId: '',
      project: '',
      issueType: null,
      status: null,
      labels: [],
      scheduleType: 'interval',
      intervalSeconds: null,
      cronExpression: null,
      timezone: null,
      targetWorkflowId: '',
    },
  };
}

/**
 * Deserializes a stored profile into this sheet's edited (camelCase) shape,
 * seeding an edit form's `defaultValues` (spec 074 US1). `targetWorkflowId`
 * (a `continue_task` scheduled profile's UI-only step-picker scope,
 * `ScheduledTriggerProfileFormValues`'s own doc comment) is persisted as the
 * profile's top-level `target_workflow_id` and seeded from it here, so a
 * previously-chosen scoping workflow (and its already-configured target
 * step id) both stay pre-selected on reopen.
 *
 * Falls back to this driver's blank defaults if the stored `config` doesn't
 * parse against its own schema — the same "never guess, never throw, just
 * say less" posture `TriggerProfileList`'s `actionKindLabel` documents for
 * its own read of the identical data — so a profile whose config predates a
 * schema change still opens for editing rather than crashing the sheet.
 */
function triggerProfileViewToSheetFormValues(
  profile: TriggerProfileView,
): SheetFormValues {
  if (profile.type === 'jira') {
    const parsed = JiraTriggerConfigSchema.safeParse(profile.config);
    if (!parsed.success) {
      return jiraDefaults(profile.name, profile.description, profile.enabled);
    }
    return {
      driver: 'jira',
      name: profile.name,
      description: profile.description,
      enabled: profile.enabled,
      action: fromTriggerActionWire(parsed.data.action),
      config: {
        connectionId: profile.connection_id ?? '',
        project: parsed.data.project,
        issueType: parsed.data.issue_type ?? null,
        status: parsed.data.status ?? null,
        labels: parsed.data.labels ?? [],
        scheduleType: profile.schedule_type === 'cron' ? 'cron' : 'interval',
        intervalSeconds: profile.interval_seconds,
        cronExpression: profile.cron_expression,
        timezone: profile.timezone,
        targetWorkflowId: profile.target_workflow_id ?? '',
      },
    };
  }

  if (profile.type === 'github' || profile.type === 'gitlab') {
    const parsed = ScheduledTriggerConfigSchema.safeParse(profile.config);
    if (!parsed.success) {
      return scheduledDefaults(
        profile.type,
        profile.name,
        profile.description,
        profile.enabled,
      );
    }
    return {
      driver: profile.type,
      name: profile.name,
      description: profile.description,
      enabled: profile.enabled,
      action: fromTriggerActionWire(parsed.data.action),
      config: {
        connectionId: profile.connection_id ?? '',
        repositories: parsed.data.repositories,
        watch: parsed.data.watch ?? [],
        scheduleType: profile.schedule_type === 'cron' ? 'cron' : 'interval',
        intervalSeconds: profile.interval_seconds,
        cronExpression: profile.cron_expression,
        timezone: profile.timezone,
        targetWorkflowId: profile.target_workflow_id ?? '',
      },
    };
  }

  const parsed = WorkspaceTaskChangeConfigSchema.safeParse(
    withDefaultedMode(profile.config),
  );
  if (!parsed.success) {
    return workspaceTaskChangeDefaults(
      profile.name,
      profile.description,
      profile.enabled,
    );
  }
  return {
    driver: 'workspace_task_change',
    name: profile.name,
    description: profile.description,
    enabled: profile.enabled,
    action: fromTriggerActionWire(parsed.data.action),
    config: {
      mode: parsed.data.mode,
      workflowId: parsed.data.mode === 'event' ? parsed.data.workflow_id : '',
      fromStepId: parsed.data.mode === 'event' ? parsed.data.from_step_id : '',
      toStepId: parsed.data.mode === 'event' ? parsed.data.to_step_id : '',
      scheduleType: profile.schedule_type === 'cron' ? 'cron' : 'interval',
      intervalSeconds: profile.interval_seconds,
      cronExpression: profile.cron_expression,
      timezone: profile.timezone,
    },
  };
}

/**
 * Configure a trigger profile: choose a driver (a manual, watched-transition
 * profile, or a scheduled GitHub/GitLab profile), then its driver-specific
 * fields, then one action — or edit an existing one's fields with `type`
 * fixed.
 *
 * A fully controlled form component — mirrors `SecretFormSheet`'s split
 * between the page (owns the mutation hook, `isPending`/`error`) and the
 * sheet (owns only form state and calls `onSubmit`) — so this component is
 * testable without a network or a rendered `TriggersPage`.
 *
 * The action-configuration fields (Create New Task / Continue Existing
 * Task) are `TriggerActionFields` (research.md D1), shared unchanged across
 * every driver (FR-009). `TriggerActionFields`' own input-row validity is
 * reached via its `onRowsValidityChange` callback (tracked in `rowsInvalid`
 * state) rather than an imperative ref — reading a ref during render is
 * what this repo's `react-hooks/refs` lint rule forbids, and
 * `handleSubmit`'s callback is itself created during render.
 */
export function TriggerProfileFormSheet({
  mode,
  workspaceId,
  open,
  isPending,
  error,
  onSubmit,
  onClearError,
  onCancel,
}: {
  mode: TriggerProfileFormMode;
  workspaceId: string;
  open: boolean;
  isPending: boolean;
  error: unknown;
  onSubmit: (
    values:
      | TriggerProfileFormValues
      | ScheduledTriggerProfileFormValues
      | JiraTriggerProfileFormValues,
  ) => void;
  onClearError: () => void;
  onCancel: () => void;
}) {
  const editing = mode.kind === 'edit';
  const { workflows } = useWorkflows(workspaceId);

  // What was typed into a new trigger form and not saved (spec 124 US2): one
  // draft per workspace, through the same mechanism the connection form uses.
  const restored = useRestoredDraft(
    useTriggerDraftStore,
    workspaceId,
    !editing,
  );
  const [draftStartedAt, setDraftStartedAt] = useState<string | null>(
    restored?.startedAt ?? null,
  );

  const {
    control,
    register,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors, isSubmitted, isDirty },
  } = useForm<SheetFormValues>({
    resolver: zodResolver(sheetFormSchema),
    defaultValues: editing
      ? triggerProfileViewToSheetFormValues(mode.profile)
      : (restored?.values ?? workspaceTaskChangeDefaults('', null, true)),
  });

  const draftWatched = useWatch({ control });
  useDraftAutosave({
    store: useTriggerDraftStore,
    scope: workspaceId,
    enabled: !editing && open,
    isDirty,
    watched: draftWatched,
    read: getValues,
  });

  function discardDraft() {
    useTriggerDraftStore.getState().clearDraft(workspaceId);
    setDraftStartedAt(null);
    reset(workspaceTaskChangeDefaults('', null, true));
  }

  // Named single-field watches only — never a whole-object `useWatch({control})`
  // against this discriminated union, which degrades to a `DeepPartial` that
  // breaks the exact narrowing this component relies on throughout (the same
  // lesson `ConnectionFormSheet` documents for its own provider union).
  const driver = useWatch({ control, name: 'driver' });
  const action = useWatch({ control, name: 'action' });
  const config = useWatch({ control, name: 'config' });

  const watchedWorkflow =
    driver === 'workspace_task_change' && 'workflowId' in config
      ? workflows.find((wf) => wf.id === config.workflowId)
      : undefined;
  const targetWorkflow =
    driver !== 'workspace_task_change' && 'targetWorkflowId' in config
      ? workflows.find((wf) => wf.id === config.targetWorkflowId)
      : undefined;
  const scopingWorkflowId =
    driver === 'workspace_task_change'
      ? watchedWorkflow?.id
      : targetWorkflow?.id;

  /** `workspace_task_change` only accepts `create_task` (FR-007) — the
   *  backend rejects `continue_task` for this driver (Spec 076); every
   *  scheduled driver (`github`/`gitlab`) is unaffected (FR-008). */
  const availableActionKinds: TriggerActionKind[] =
    driver === 'workspace_task_change'
      ? ['create_task']
      : ['create_task', 'continue_task'];

  const serverError = isTriggerError(error) ? error : undefined;
  const genericError =
    error && !serverError ? 'The request failed. Please try again.' : undefined;
  const formMessage = serverError?.message ?? genericError;

  /**
   * `errors.action`/`errors.config`'s RHF-inferred types merge every branch
   * of the driver union (and, for `action`, the nested action union too), so
   * a plain dotted access does not type-check no matter which branch is
   * active — the same narrowing this feature already applies for `action`
   * alone, extended to `config`.
   */
  function actionFieldError(name: string): string | undefined {
    const actionErrors = errors.action as
      Record<string, { message?: string } | undefined> | undefined;
    return actionErrors?.[name]?.message;
  }

  function configFieldError(name: string): string | undefined {
    const configErrors = errors.config as
      Record<string, { message?: string } | undefined> | undefined;
    return configErrors?.[name]?.message;
  }

  const nameId = useId();
  const descriptionId = useId();
  const enabledId = useId();
  const driverGroupId = useId();
  const driverLabelId = useId();
  const repositoriesId = useId();
  const modeGroupId = useId();
  const modeLabelId = useId();
  const jiraProjectId = useId();
  const jiraIssueTypeId = useId();
  const jiraStatusId = useId();
  const jiraLabelsId = useId();

  /** Tracks `TriggerActionFields`' own input-row validity (research.md's
   *  row editor has no shape any schema here can express) — updated via its
   *  `onRowsValidityChange` callback, not read from a ref. */
  const [rowsInvalid, setRowsInvalid] = useState(false);

  /**
   * The Repositories textarea's own raw text, kept apart from
   * `config.repositories` (the derived, trimmed, filtered array RHF holds).
   * Deriving the textarea's displayed `value` from that array instead of
   * this raw string is the classic controlled-input bug: filtering out the
   * empty segment a trailing separator produces mid-typing snaps the
   * displayed value backward on every comma/newline, which fights the
   * user's own keystrokes and garbles anything typed after the first
   * separator. This raw string is the only thing the textarea ever
   * displays; `config.repositories` is derived from it on every change but
   * never fed back into it.
   */
  const [repositoriesText, setRepositoriesText] = useState(() =>
    'repositories' in config ? config.repositories.join('\n') : '',
  );

  /** Same reasoning as `repositoriesText` above, for Jira's comma-separated
   *  `labels` field. */
  const [labelsText, setLabelsText] = useState(() =>
    'labels' in config ? config.labels.join(', ') : '',
  );

  function switchDriver(next: Driver) {
    // Never reachable in edit mode — the driver chooser renders as fixed
    // text there (FR-003), with no radio input to trigger this. Guarded
    // anyway, defensively, rather than assuming the render branch below is
    // the only thing that can ever call it.
    if (editing) return;
    const { name, description, enabled } = getValues();
    reset(
      next === 'workspace_task_change'
        ? workspaceTaskChangeDefaults(name, description, enabled)
        : next === 'jira'
          ? jiraDefaults(name, description, enabled)
          : scheduledDefaults(next, name, description, enabled),
    );
    setRowsInvalid(false);
    setRepositoriesText('');
    setLabelsText('');
    onClearError();
  }

  /**
   * Switching activation mode (Spec 082 US1) clears the fields meaningful
   * only to the mode being left — a from/to-step selection or a schedule
   * value left over from the other mode must never be silently resubmitted
   * (Edge Cases) — mirroring `switchDriver`'s own full-reset approach, but
   * scoped to just this one branch's fields rather than the whole form.
   */
  function switchMode(next: WorkspaceTaskChangeMode) {
    if (driver !== 'workspace_task_change' || !('mode' in config)) return;
    setValue('config', {
      mode: next,
      workflowId: '',
      fromStepId: '',
      toStepId: '',
      scheduleType: 'interval',
      intervalSeconds: null,
      cronExpression: null,
      timezone: null,
    });
    onClearError();
  }

  const submit = handleSubmit((values) => {
    if (rowsInvalid) return;
    if (values.driver === 'workspace_task_change') {
      onSubmit({
        name: values.name,
        description: values.description,
        enabled: values.enabled,
        action: values.action,
        ...values.config,
      });
      return;
    }
    if (values.driver === 'jira') {
      onSubmit({
        name: values.name,
        description: values.description,
        enabled: values.enabled,
        type: values.driver,
        action: values.action,
        ...values.config,
      });
      return;
    }
    onSubmit({
      name: values.name,
      description: values.description,
      enabled: values.enabled,
      type: values.driver,
      action: values.action,
      ...values.config,
    });
  });

  // Making the connection this form needs without leaving it (spec 124 US1).
  // The stacked sheet hands back a connection; an enabled one is selected and
  // said so beside the field, a disabled one is not — the picker lists enabled
  // connections only — and the line says why. Focus returns to the picker.
  const [creatingConnection, setCreatingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<string | null>(null);
  const connectionFieldRef = useRef<HTMLButtonElement>(null);

  const connectionRequiredButMissing =
    driver !== 'workspace_task_change' &&
    'connectionId' in config &&
    config.connectionId.trim() === '';

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(next) => {
          // Never dismiss a save in flight — the mutation must resolve against
          // this sheet, not an already-dismissed one.
          if (isPending) return;
          if (!next) onCancel();
        }}
      >
        <SheetContent className="sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>
              {editing ? `Edit ${mode.profile.name}` : 'New trigger'}
            </SheetTitle>
            <SheetDescription>
              {editing
                ? 'The type cannot be changed. To watch something else, create a new trigger.'
                : 'Watch a task transition, or a GitHub/GitLab repository or Jira project/issue filter on a schedule — then either create a task or continue the task that triggered it.'}
            </SheetDescription>
          </SheetHeader>

          <form
            onSubmit={(event) => {
              void submit(event);
            }}
            className="flex min-h-0 flex-1 flex-col"
          >
            <SheetBody className="flex flex-col gap-4">
              {draftStartedAt ? (
                <DraftNote
                  noun="trigger"
                  startedAt={draftStartedAt}
                  onDiscard={discardDraft}
                />
              ) : null}
              <div className="flex flex-col gap-1.5">
                <span
                  id={driverLabelId}
                  className="text-sm leading-none font-medium"
                >
                  Type
                </span>
                {editing ? (
                  <p className="text-muted-foreground text-sm">
                    {
                      DRIVER_CHOICES.find((choice) => choice.value === driver)
                        ?.label
                    }{' '}
                    — set when this trigger was created and fixed for its
                    lifetime.
                  </p>
                ) : (
                  <div
                    role="radiogroup"
                    aria-labelledby={driverLabelId}
                    className="grid gap-2 sm:grid-cols-3"
                  >
                    {DRIVER_CHOICES.map((choice) => (
                      <label
                        key={choice.value}
                        className={cn(
                          'border-border hover:bg-muted/50 flex cursor-pointer flex-col gap-1 rounded-lg border p-3 transition-colors',
                          driver === choice.value && 'border-ring bg-muted/50',
                        )}
                      >
                        <span className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={driverGroupId}
                            value={choice.value}
                            checked={driver === choice.value}
                            disabled={isPending}
                            onChange={() => {
                              switchDriver(choice.value);
                            }}
                            className="accent-primary"
                          />
                          <span className="text-sm font-medium">
                            {choice.label}
                          </span>
                        </span>
                        <span className="text-muted-foreground text-xs">
                          {choice.consequence}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor={nameId}>Name</Label>
                <Input
                  id={nameId}
                  {...register('name')}
                  disabled={isPending}
                  autoFocus
                  aria-invalid={errors.name ? true : undefined}
                />
                {errors.name ? (
                  <p className="text-destructive text-xs">
                    {errors.name.message}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor={descriptionId}>
                  Description{' '}
                  <span className="text-muted-foreground font-normal">
                    (optional)
                  </span>
                </Label>
                <Input
                  id={descriptionId}
                  disabled={isPending}
                  {...register('description', {
                    // RHF invokes `setValueAs` against the field's current
                    // value at registration time too, not only on change — and
                    // the default value here is `null` (never a string), so a
                    // guard is required, not just at submission.
                    setValueAs: (v: unknown) =>
                      typeof v === 'string' && v.trim() !== '' ? v : null,
                  })}
                />
              </div>

              {editing ? null : (
                <div className="flex items-center gap-2">
                  <input
                    id={enabledId}
                    type="checkbox"
                    className="accent-primary"
                    disabled={isPending}
                    {...register('enabled')}
                  />
                  <Label htmlFor={enabledId} className="font-normal">
                    Enabled
                  </Label>
                </div>
              )}

              {driver === 'workspace_task_change' && 'mode' in config ? (
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1.5">
                    <span
                      id={modeLabelId}
                      className="text-sm leading-none font-medium"
                    >
                      Activation
                    </span>
                    <div
                      role="radiogroup"
                      aria-labelledby={modeLabelId}
                      className="grid gap-2 sm:grid-cols-2"
                    >
                      {(
                        [
                          {
                            value: 'event' as const,
                            label: 'A workflow transition happens',
                          },
                          {
                            value: 'schedule' as const,
                            label: 'On a schedule',
                          },
                        ] satisfies {
                          value: WorkspaceTaskChangeMode;
                          label: string;
                        }[]
                      ).map((choice) => (
                        <label
                          key={choice.value}
                          className={cn(
                            'border-border hover:bg-muted/50 flex cursor-pointer items-center gap-2 rounded-lg border p-3 transition-colors',
                            config.mode === choice.value &&
                              'border-ring bg-muted/50',
                          )}
                        >
                          <input
                            type="radio"
                            name={modeGroupId}
                            value={choice.value}
                            checked={config.mode === choice.value}
                            disabled={isPending}
                            onChange={() => {
                              switchMode(choice.value);
                            }}
                            className="accent-primary"
                          />
                          <span className="text-sm font-medium">
                            {choice.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {config.mode === 'event' ? (
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="flex flex-col gap-1.5">
                        <Label>Watched workflow</Label>
                        <WorkflowSelect
                          workspaceId={workspaceId}
                          value={config.workflowId}
                          disabled={isPending}
                          aria-label="Watched workflow"
                          onChange={(next) => {
                            // Selections downstream of the watched workflow are
                            // no longer valid once it changes. A continue_task
                            // action's source/target steps are both bound to
                            // the watched workflow (see `TriggerActionFields`),
                            // so they are just as stale as from/to-step once
                            // this changes.
                            setValue('config', {
                              ...config,
                              workflowId: next,
                              fromStepId: '',
                              toStepId: '',
                            });
                            if (action.kind === 'continue_task') {
                              setValue('action', {
                                ...action,
                                sourceStepId: '',
                                targetStepId: '',
                              });
                            }
                            onClearError();
                          }}
                        />
                        {configFieldError('workflowId') ? (
                          <p className="text-destructive text-xs">
                            {configFieldError('workflowId')}
                          </p>
                        ) : null}
                      </div>
                      <div />

                      <div className="flex flex-col gap-1.5">
                        <Label>From step</Label>
                        <WorkflowStepSelect
                          workflow={watchedWorkflow}
                          value={config.fromStepId}
                          disabled={isPending}
                          allowAnyStep
                          aria-label="From step"
                          onChange={(next) => {
                            setValue('config', { ...config, fromStepId: next });
                            onClearError();
                          }}
                        />
                        {configFieldError('fromStepId') ? (
                          <p className="text-destructive text-xs">
                            {configFieldError('fromStepId')}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Label>To step</Label>
                        <WorkflowStepSelect
                          workflow={watchedWorkflow}
                          value={config.toStepId}
                          disabled={isPending}
                          allowAnyStep
                          aria-label="To step"
                          onChange={(next) => {
                            setValue('config', { ...config, toStepId: next });
                            onClearError();
                          }}
                        />
                        {configFieldError('toStepId') ? (
                          <p className="text-destructive text-xs">
                            {configFieldError('toStepId')}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <ScheduleFields
                      value={config}
                      onChange={(next) => {
                        setValue('config', { ...config, ...next });
                      }}
                      fieldError={configFieldError}
                      onClearError={onClearError}
                      disabled={isPending}
                    />
                  )}
                </div>
              ) : driver !== 'workspace_task_change' &&
                'connectionId' in config ? (
                <div className="flex flex-col gap-3 border-t pt-3">
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-0.5">
                      <Label>Connection</Label>
                      <RequiredMark />
                    </div>
                    <ConnectionSelect
                      ref={connectionFieldRef}
                      workspaceId={workspaceId}
                      providerType={driver}
                      value={config.connectionId}
                      disabled={isPending}
                      aria-label="Connection"
                      onChange={(next) => {
                        setValue('config', { ...config, connectionId: next });
                        setConnectionStatus(null);
                        onClearError();
                      }}
                      onCreateRequest={() => {
                        setCreatingConnection(true);
                      }}
                    />
                    {connectionStatus ? (
                      <p
                        role="status"
                        aria-live="polite"
                        className="text-muted-foreground text-xs"
                      >
                        {connectionStatus}
                      </p>
                    ) : null}
                    {configFieldError('connectionId') ? (
                      <p className="text-destructive text-xs">
                        {configFieldError('connectionId')}
                      </p>
                    ) : (
                      <p className="text-muted-foreground text-xs">
                        Only enabled connections for this provider are offered.
                      </p>
                    )}
                  </div>

                  {/* Repository, watch, and schedule fields all depend on a
                    connection existing first — disabled with a guiding hint
                    until one is chosen, rather than an empty or broken
                    picker (Edge Cases). */}
                  <fieldset
                    disabled={isPending || connectionRequiredButMissing}
                    className="flex flex-col gap-3 disabled:opacity-50"
                  >
                    {connectionRequiredButMissing ? (
                      <p className="text-muted-foreground text-xs">
                        Choose a connection above before configuring
                        repositories or a schedule.
                      </p>
                    ) : null}

                    {'repositories' in config ? (
                      <>
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-0.5">
                            <Label htmlFor={repositoriesId}>Repositories</Label>
                            <RequiredMark />
                          </div>
                          <Textarea
                            id={repositoriesId}
                            rows={3}
                            className="font-mono"
                            placeholder={
                              driver === 'github'
                                ? 'org/repo\nanother-org/another-repo'
                                : 'group/project\nanother-group/another-project'
                            }
                            value={repositoriesText}
                            disabled={isPending || connectionRequiredButMissing}
                            onChange={(event) => {
                              const raw = event.target.value;
                              setRepositoriesText(raw);
                              setValue('config', {
                                ...config,
                                repositories: raw
                                  .split(/[\n,]/)
                                  .map((entry) => entry.trim())
                                  .filter((entry) => entry !== ''),
                              });
                              onClearError();
                            }}
                          />
                          {configFieldError('repositories') ? (
                            <p className="text-destructive text-xs">
                              {configFieldError('repositories')}
                            </p>
                          ) : (
                            <p className="text-muted-foreground text-xs">
                              One or more, one per line or comma-separated.
                            </p>
                          )}
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <Label>
                            Watched activity{' '}
                            <span className="text-muted-foreground font-normal">
                              (optional)
                            </span>
                          </Label>
                          <div className="flex flex-wrap gap-3">
                            {WATCHED_EVENT_CHOICES.map((choice) => (
                              <label
                                key={choice.value}
                                className="flex items-center gap-1.5 text-sm"
                              >
                                <input
                                  type="checkbox"
                                  className="accent-primary"
                                  disabled={
                                    isPending || connectionRequiredButMissing
                                  }
                                  checked={config.watch.includes(choice.value)}
                                  onChange={(event) => {
                                    setValue('config', {
                                      ...config,
                                      watch: event.target.checked
                                        ? [...config.watch, choice.value]
                                        : config.watch.filter(
                                            (v) => v !== choice.value,
                                          ),
                                    });
                                  }}
                                />
                                {choice.label}
                              </label>
                            ))}
                          </div>
                          <p className="text-muted-foreground text-xs">
                            Leave every box unchecked to watch everything
                            (FR-008&apos;s default).
                          </p>
                        </div>
                      </>
                    ) : 'project' in config ? (
                      <>
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-0.5">
                            <Label htmlFor={jiraProjectId}>Project key</Label>
                            <RequiredMark />
                          </div>
                          <Input
                            id={jiraProjectId}
                            value={config.project}
                            disabled={isPending || connectionRequiredButMissing}
                            placeholder="BACKEND"
                            className="font-mono"
                            onChange={(event) => {
                              setValue('config', {
                                ...config,
                                project: event.target.value,
                              });
                              onClearError();
                            }}
                          />
                          {configFieldError('project') ? (
                            <p className="text-destructive text-xs">
                              {configFieldError('project')}
                            </p>
                          ) : null}
                        </div>

                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="flex flex-col gap-1.5">
                            <Label htmlFor={jiraIssueTypeId}>
                              Issue type{' '}
                              <span className="text-muted-foreground font-normal">
                                (optional)
                              </span>
                            </Label>
                            <Input
                              id={jiraIssueTypeId}
                              value={config.issueType ?? ''}
                              disabled={
                                isPending || connectionRequiredButMissing
                              }
                              placeholder="Bug"
                              onChange={(event) => {
                                const next = event.target.value;
                                setValue('config', {
                                  ...config,
                                  issueType: next.trim() === '' ? null : next,
                                });
                              }}
                            />
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <Label htmlFor={jiraStatusId}>
                              Status{' '}
                              <span className="text-muted-foreground font-normal">
                                (optional)
                              </span>
                            </Label>
                            <Input
                              id={jiraStatusId}
                              value={config.status ?? ''}
                              disabled={
                                isPending || connectionRequiredButMissing
                              }
                              placeholder="Ready for Development"
                              onChange={(event) => {
                                const next = event.target.value;
                                setValue('config', {
                                  ...config,
                                  status: next.trim() === '' ? null : next,
                                });
                              }}
                            />
                          </div>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <Label htmlFor={jiraLabelsId}>
                            Labels{' '}
                            <span className="text-muted-foreground font-normal">
                              (optional)
                            </span>
                          </Label>
                          <Input
                            id={jiraLabelsId}
                            value={labelsText}
                            disabled={isPending || connectionRequiredButMissing}
                            placeholder="ai, urgent"
                            onChange={(event) => {
                              const raw = event.target.value;
                              setLabelsText(raw);
                              setValue('config', {
                                ...config,
                                labels: raw
                                  .split(',')
                                  .map((label) => label.trim())
                                  .filter((label) => label !== ''),
                              });
                            }}
                          />
                          <p className="text-muted-foreground text-xs">
                            One or more, comma-separated. Leave blank to match
                            any labels.
                          </p>
                        </div>
                      </>
                    ) : null}

                    <ScheduleFields
                      value={config}
                      onChange={(next) => {
                        setValue('config', { ...config, ...next });
                      }}
                      fieldError={configFieldError}
                      onClearError={onClearError}
                      disabled={isPending || connectionRequiredButMissing}
                    />

                    {action.kind === 'continue_task' ? (
                      <div className="flex flex-col gap-1.5">
                        <Label>
                          Target workflow{' '}
                          <span className="text-muted-foreground font-normal">
                            (for step selection only)
                          </span>
                        </Label>
                        <TargetWorkflowSelect
                          workspaceId={workspaceId}
                          value={config.targetWorkflowId}
                          disabled={isPending || connectionRequiredButMissing}
                          onChange={(next) => {
                            setValue('config', {
                              ...config,
                              targetWorkflowId: next,
                            });
                            // Submitted as the profile's top-level
                            // `target_workflow_id` and also scopes
                            // TriggerActionFields' step pickers below, so
                            // stale source/target steps from the previous
                            // scoping workflow must clear too.
                            if (action.kind === 'continue_task') {
                              setValue('action', {
                                ...action,
                                sourceStepId: '',
                                targetStepId: '',
                              });
                            }
                          }}
                        />
                      </div>
                    ) : null}
                  </fieldset>
                </div>
              ) : null}

              <TriggerActionFields
                workspaceId={workspaceId}
                driverType={driver}
                action={action}
                onActionChange={(next) => {
                  setValue('action', next);
                }}
                actionFieldError={actionFieldError}
                disabled={
                  isPending ||
                  (driver !== 'workspace_task_change' &&
                    connectionRequiredButMissing)
                }
                onClearError={onClearError}
                scopingWorkflowId={scopingWorkflowId}
                isSubmitted={isSubmitted}
                onRowsValidityChange={setRowsInvalid}
                availableActionKinds={availableActionKinds}
              />

              {formMessage ? (
                <p role="alert" className="text-destructive text-sm">
                  {formMessage}
                </p>
              ) : null}
            </SheetBody>

            <SheetFooter>
              <SheetClose
                render={<Button type="button" variant="outline" />}
                disabled={isPending}
              >
                Cancel
              </SheetClose>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? editing
                    ? 'Saving…'
                    : 'Creating…'
                  : editing
                    ? 'Save changes'
                    : 'Create trigger'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {/* Beside the sheet, so the layering is Base UI's own: Esc closes the
          stacked sheet alone, and this form is intact behind it. */}
      {driver !== 'workspace_task_change' && 'connectionId' in config ? (
        <CreateConnectionSheet
          workspaceId={workspaceId}
          open={creatingConnection}
          providerType={driver}
          finalFocus={connectionFieldRef}
          onCreated={(connection) => {
            if (connection.enabled) {
              setValue('config', { ...config, connectionId: connection.id });
              setConnectionStatus(`Connection ${connection.name} added`);
              onClearError();
            } else {
              setConnectionStatus(
                `Connection ${connection.name} was saved disabled — enable it on the Connections page to use it here`,
              );
            }
          }}
          onClose={() => {
            setCreatingConnection(false);
          }}
        />
      ) : null}
    </>
  );
}
